import { useEffect, useState } from "react";
import { Link, useParams, useNavigate } from "react-router-dom";
import api from "@/lib/api";
import useSEO from "@/lib/useSEO";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Clock, ArrowLeft, ArrowRight, Share2 } from "lucide-react";
import { toast } from "sonner";
import AdSlot from "@/components/AdSlot";
import NewsletterForm from "@/components/NewsletterForm";

const SITE = "https://spa-report-hub.emergent.host";

export default function BlogPost() {
  const { slug } = useParams();
  const navigate = useNavigate();
  const [post, setPost] = useState(null);
  const [related, setRelated] = useState([]);
  const [loading, setLoading] = useState(true);
  const [notFound, setNotFound] = useState(false);

  useEffect(() => {
    setLoading(true);
    setNotFound(false);
    api
      .get(`/blog/posts/${slug}`)
      .then((r) => setPost(r.data))
      .catch((e) => {
        if (e.response?.status === 404) setNotFound(true);
      })
      .finally(() => setLoading(false));
    api.get("/blog/posts", { params: { limit: 4 } }).then((r) => setRelated(r.data.posts || []));
    window.scrollTo({ top: 0, behavior: "smooth" });
  }, [slug]);

  useSEO(
    post
      ? {
          title: post.meta_title || `${post.title} | DoctoSpa`,
          description: post.meta_description || post.excerpt,
          image: post.cover_url,
          url: `${SITE}/blog/${post.slug}`,
          type: "article",
          jsonLd: {
            "@context": "https://schema.org",
            "@type": "BlogPosting",
            headline: post.title,
            description: post.meta_description || post.excerpt,
            image: post.cover_url ? [post.cover_url] : undefined,
            author: { "@type": "Organization", name: post.author_name || "DoctoSpa" },
            publisher: {
              "@type": "Organization",
              name: "DoctoSpa",
              logo: {
                "@type": "ImageObject",
                url: `${SITE}/favicon.ico`,
              },
            },
            datePublished: post.published_at,
            dateModified: post.updated_at || post.published_at,
            mainEntityOfPage: `${SITE}/blog/${post.slug}`,
          },
        }
      : { title: "Article — DoctoSpa" }
  );

  const share = async () => {
    const url = `${SITE}/blog/${post.slug}`;
    if (navigator.share) {
      try {
        await navigator.share({ title: post.title, text: post.excerpt, url });
      } catch {}
    } else {
      navigator.clipboard.writeText(url);
      toast.success("Lien copié dans le presse-papiers");
    }
  };

  if (loading) return <div className="mx-auto max-w-3xl px-6 py-24 text-sm text-muted-foreground">Chargement…</div>;
  if (notFound || !post) {
    return (
      <div className="mx-auto max-w-3xl px-6 py-24 text-center" data-testid="blog-not-found">
        <h1 className="font-display text-2xl font-bold">Article introuvable</h1>
        <p className="mt-3 text-sm text-muted-foreground">Cet article n'existe pas ou a été retiré.</p>
        <Button className="mt-6 rounded-full" onClick={() => navigate("/blog")} data-testid="blog-back-btn">
          Retour au blog
        </Button>
      </div>
    );
  }

  const filteredRelated = related.filter((r) => r.slug !== post.slug).slice(0, 3);

  return (
    <article className="mx-auto max-w-3xl px-6 lg:px-10 py-12 lg:py-16" data-testid="blog-post-page">
      <Link to="/blog" className="inline-flex items-center gap-1 text-sm text-muted-foreground hover:text-foreground transition-colors" data-testid="blog-back-link">
        <ArrowLeft className="h-4 w-4" /> Blog
      </Link>

      <div className="mt-8 flex items-center gap-2 text-xs">
        <Badge variant="secondary" className="rounded-full" data-testid="blog-post-category">{post.category}</Badge>
        <span className="text-muted-foreground flex items-center gap-1">
          <Clock className="h-3 w-3" /> {post.reading_time_min} min de lecture
        </span>
        {post.published_at && (
          <span className="text-muted-foreground">
            · {new Date(post.published_at).toLocaleDateString("fr-FR", { day: "numeric", month: "long", year: "numeric" })}
          </span>
        )}
      </div>

      <h1 className="mt-5 font-display text-3xl lg:text-5xl font-black tracking-tighter leading-[1.05]" data-testid="blog-post-title">
        {post.title}
      </h1>

      {post.excerpt && (
        <p className="mt-5 text-lg text-muted-foreground leading-relaxed" data-testid="blog-post-excerpt">
          {post.excerpt}
        </p>
      )}

      <div className="mt-6 flex items-center justify-between border-y border-black/10 py-4">
        <span className="text-xs text-muted-foreground">
          Par <span className="font-semibold text-foreground">{post.author_name}</span>
        </span>
        <Button variant="ghost" size="sm" onClick={share} data-testid="blog-share-btn">
          <Share2 className="h-4 w-4 mr-2" /> Partager
        </Button>
      </div>

      {post.cover_url && (
        <img
          src={post.cover_url}
          alt={post.title}
          className="mt-8 rounded-2xl w-full aspect-[16/9] object-cover border border-black/10"
        />
      )}

      <div
        className="blog-content mt-10 max-w-none leading-relaxed text-[15px] text-foreground/90"
        dangerouslySetInnerHTML={{ __html: post.content_html }}
        data-testid="blog-post-content"
      />

      {/* AD SLOT — after article content */}
      <div className="mt-12">
        <AdSlot placement="blog" />
      </div>

      {/* Tags */}
      {post.tags?.length > 0 && (
        <div className="mt-12 flex flex-wrap gap-2" data-testid="blog-post-tags">
          {post.tags.map((t) => (
            <span key={t} className="text-xs px-2.5 py-1 rounded-full bg-black/5 text-muted-foreground">
              #{t}
            </span>
          ))}
        </div>
      )}

      {/* Newsletter capture */}
      <div className="mt-14">
        <NewsletterForm source="blog" />
      </div>

      {/* CTA */}
      <div className="mt-14 rounded-2xl bg-secondary text-white p-8">
        <div className="eyebrow text-white/60">Diagnostic gratuit en 2 min</div>
        <h3 className="mt-2 font-display text-2xl font-bold tracking-tight">
          Votre spa présente un symptôme similaire ?
        </h3>
        <p className="mt-2 text-sm text-white/70">
          Notre IA Vision analyse votre panne à partir d'une photo et vous propose la cause probable + le coût de réparation.
        </p>
        <Link to="/diagnostic">
          <Button className="mt-6 bg-primary hover:bg-primary/90 rounded-full h-11 px-6" data-testid="blog-post-cta-diagnostic">
            Lancer mon diagnostic
          </Button>
        </Link>
      </div>

      {/* Related */}
      {filteredRelated.length > 0 && (
        <div className="mt-20">
          <div className="eyebrow">À lire aussi</div>
          <h3 className="mt-3 font-display text-2xl font-bold">Continuer avec…</h3>
          <div className="mt-8 grid gap-6 md:grid-cols-2">
            {filteredRelated.map((r) => (
              <Link key={r.id} to={`/blog/${r.slug}`} className="group" data-testid={`blog-related-${r.slug}`}>
                <Card className="h-full p-6 border-black/10 hover:border-primary/40 hover:shadow-lg transition-all">
                  <Badge variant="secondary" className="rounded-full">{r.category}</Badge>
                  <h4 className="mt-3 font-display font-bold text-base leading-tight group-hover:text-primary transition-colors">
                    {r.title}
                  </h4>
                  <p className="mt-2 text-xs text-muted-foreground line-clamp-2">{r.excerpt}</p>
                  <span className="mt-4 inline-flex items-center gap-1 text-xs font-semibold text-primary">
                    Lire <ArrowRight className="h-3 w-3" />
                  </span>
                </Card>
              </Link>
            ))}
          </div>
        </div>
      )}
    </article>
  );
}
